import { useContext, useEffect, useEffectEvent, useState } from 'react'
import { useNavigate } from 'react-router'
import { Alert, Button, CircularProgress, Divider, Grid, Typography, useMediaQuery, useTheme } from '@mui/material'

import { AppContext } from '../../providers/app-provider'
import { useAuthentication } from '../../providers/auth-provider'
import Api from '../../utils/api'
import { RELEASE_NOTES_TAG } from '../../utils/constants'
import useDocumentTitle from '../../utils/hooks/useDocumentTitle'
import useSharedStyles from '../../utils/shared.styles'

import useStyles from './index.styles'
import ReleaseNote, { hasWriteReleasePermission } from './release-note'

const ReleaseNotes = () => {
  const classes = useStyles
  const sharedClasses = useSharedStyles

  const theme = useTheme()
  const smallScreenMatches = useMediaQuery(() => theme.breakpoints.down('sm'))

  const navigate = useNavigate()

  const { auth, ownPermissions } = useAuthentication()
  const { pageRefreshCounter } = useContext(AppContext)

  const [releaseNotes, setReleaseNotes] = useState<IReleaseNoteType[]>([])
  const [notesLoading, setNotesLoading] = useState<boolean>(false)
  const [notesError, setNotesError] = useState<string>('')

  useDocumentTitle('Meshify Central')

  const getReleaseNotes = useEffectEvent(async () => {
    if (!auth.user?.access_token) return
    setNotesLoading(true)
    const res = await Api.get(`/api/v2/releases?tags=${RELEASE_NOTES_TAG}`)
    setNotesLoading(false)

    if (!res.ok) {
      setNotesError('Something went wrong and we could not load release notes at this time. Please try again later')
    } else {
      setReleaseNotes(res.body.releases)
    }
  })
  useEffect(() => {
    getReleaseNotes()
  }, [pageRefreshCounter])

  return (
    <Grid container={true} spacing={1}>
      <Grid size={smallScreenMatches ? 7 : 8}>
        <Typography variant={smallScreenMatches ? 'h5' : 'h4'}>Central Release Notes</Typography>
      </Grid>
      <Grid
        container={true}
        size={smallScreenMatches ? 5 : 4}
        sx={[sharedClasses.centerContentVertical, { justifyContent: 'flex-end', marginTop: smallScreenMatches ? 0.5 : 0 }]}
      >
        {hasWriteReleasePermission(ownPermissions) && (
          <Button onClick={() => navigate('/release-notes/new')} aria-label="Add Release Note" size={smallScreenMatches ? 'small' : 'medium'}>
            Add new release note
          </Button>
        )}
      </Grid>
      {!notesLoading && notesError && (
        <Grid container={true} sx={[sharedClasses.centerContentHorizontal, classes.errorMessage]}>
          <Alert severity="error">{notesError}</Alert>
        </Grid>
      )}
      {notesLoading && !notesError && (
        <Grid container={true} sx={sharedClasses.centerContentHorizontal}>
          <CircularProgress />
        </Grid>
      )}
      {!notesLoading &&
        !notesError &&
        releaseNotes
          .sort((note1, note2) => new Date(note2.releaseDate).valueOf() - new Date(note1.releaseDate).valueOf())
          .map((releaseNote, idx) => (
            <Grid
              key={releaseNote.id}
              container={true}
              spacing={0}
              size={12}
              sx={{
                marginBottom: 2,
              }}
            >
              <ReleaseNote releaseNote={releaseNote} />

              {idx !== releaseNotes.length - 1 && <Divider sx={classes.divider} />}
            </Grid>
          ))}
    </Grid>
  )
}

export default ReleaseNotes
